import {
  Select as AriaSelect,
  SelectValue,
  Button,
  Popover,
  ListBox,
  ListBoxItem,
  Label,
  FieldError,
  Text,
  type SelectProps as AriaSelectProps,
  type ListBoxItemProps,
} from 'react-aria-components'
import { ChevronDownIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps<T extends object>
  extends Omit<AriaSelectProps<T>, 'children'> {
  label?: string
  description?: string
  errorMessage?: string
  placeholder?: string
  options: SelectOption[]
}

export function Select<T extends object>({
  className,
  label,
  description,
  errorMessage,
  placeholder = 'Select an option',
  options,
  isDisabled,
  isRequired,
  'aria-label': ariaLabel,
  ...props
}: SelectProps<T>) {
  // Ensure we always have an accessible label
  const accessibleLabel = label || ariaLabel || placeholder

  return (
    <AriaSelect
      className={cn('flex flex-col gap-1.5', className)}
      isDisabled={isDisabled}
      isRequired={isRequired}
      aria-label={!label ? accessibleLabel : undefined}
      {...props}
    >
      {label && (
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {label}
          {isRequired && <span className="text-error-500 ml-0.5">*</span>}
        </Label>
      )}
      <Button
        className={cn(
          'flex items-center justify-between w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-base',
          'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
          'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
          'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100',
          'dark:focus:border-brand-400 dark:focus:ring-brand-400',
          errorMessage && 'border-error-500 focus:border-error-500 focus:ring-error-500'
        )}
      >
        <SelectValue className="text-gray-900 dark:text-gray-100 placeholder:text-gray-400">
          {({ selectedText }) => selectedText || <span className="text-gray-400">{placeholder}</span>}
        </SelectValue>
        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
      </Button>
      <Popover
        className={cn(
          'w-[var(--trigger-width)] rounded-lg border border-gray-200 bg-white shadow-lg',
          'dark:bg-gray-900 dark:border-gray-700',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <ListBox className="p-1 max-h-60 overflow-auto outline-none">
          {options.map((option) => (
            <SelectItem key={option.value} id={option.value} textValue={option.label} isDisabled={option.disabled}>
              {option.label}
            </SelectItem>
          ))}
        </ListBox>
      </Popover>
      {description && !errorMessage && (
        <Text slot="description" className="text-sm text-gray-500 dark:text-gray-400">
          {description}
        </Text>
      )}
      {errorMessage && (
        <FieldError className="text-sm text-error-600 dark:text-error-400">
          {errorMessage}
        </FieldError>
      )}
    </AriaSelect>
  )
}

function SelectItem(props: ListBoxItemProps) {
  return (
    <ListBoxItem
      {...props}
      className={cn(
        'flex items-center px-3 py-2 rounded-md text-sm cursor-pointer outline-none',
        'text-gray-900 dark:text-gray-100',
        'hover:bg-gray-100 dark:hover:bg-gray-800',
        'focus:bg-gray-100 dark:focus:bg-gray-800',
        'selected:bg-brand-50 selected:text-brand-700 dark:selected:bg-brand-950 dark:selected:text-brand-300',
        'disabled:text-gray-400 disabled:cursor-not-allowed'
      )}
    />
  )
}

export default Select
